import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Car, CurrencyDollar, TrendUp, CalendarBlank, ChartLineUp, Wrench, Shield, Users } from '@phosphor-icons/react';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = {
  primary: '#FF6B35',
  secondary: '#F7931E',
  danger: '#ef4444',
  success: '#22c55e',
  muted: '#94a3b8'
};

const CAR_PRESETS = [
  { name: 'Toyota Corolla Hybrid', price: 125000, weeklyRent: 700 },
  { name: 'Toyota Prius', price: 140000, weeklyRent: 750 },
  { name: 'Skoda Fabia', price: 85000, weeklyRent: 550 },
  { name: 'Custom', price: 0, weeklyRent: 0 }
];

const TaxiFleetAnalytics = () => {
  const [formData, setFormData] = useState({
    carPreset: 'Toyota Corolla Hybrid',
    carPrice: 125000,
    downPaymentPercent: 20,
    interestRate: 8,
    loanTermYears: 5,
    weeklyRentPrice: 700,
    utilizationWeeks: 50,
    annualInsurance: 5000,
    monthlyMaintenance: 600,
    otherMonthlyCosts: 250,
    taxRate: 19,
    numberOfCars: 1
  });
  
  const [results, setResults] = useState(null);

  const calculateFleet = () => {
    const {
      carPrice, downPaymentPercent, interestRate, loanTermYears,
      weeklyRentPrice, utilizationWeeks, annualInsurance,
      monthlyMaintenance, otherMonthlyCosts, taxRate, numberOfCars
    } = formData;

    // Per car calculations
    const downPayment = carPrice * (downPaymentPercent / 100);
    const loanAmount = carPrice - downPayment;
    const monthlyInterestRate = interestRate / 100 / 12;
    const numPayments = loanTermYears * 12;
    
    // Monthly loan payment (amortization formula)
    let monthlyLoanPayment = 0;
    if (monthlyInterestRate > 0 && numPayments > 0) {
      const powerTerm = Math.pow(1 + monthlyInterestRate, numPayments);
      monthlyLoanPayment = loanAmount * (monthlyInterestRate * powerTerm) / (powerTerm - 1);
    } else {
      monthlyLoanPayment = loanAmount / numPayments;
    }

    // Income calculations
    const annualRentalIncome = weeklyRentPrice * utilizationWeeks;
    const monthlyRentalIncome = annualRentalIncome / 12;
    
    // Cost calculations (monthly)
    const monthlyInsurance = annualInsurance / 12;
    const totalMonthlyCosts = monthlyLoanPayment + monthlyInsurance + monthlyMaintenance + otherMonthlyCosts;
    
    // Profit calculations
    const monthlyGrossProfit = monthlyRentalIncome - totalMonthlyCosts;
    const monthlyTax = monthlyGrossProfit > 0 ? monthlyGrossProfit * (taxRate / 100) : 0;
    const monthlyNetProfit = monthlyGrossProfit - monthlyTax;
    
    const annualGrossProfit = monthlyGrossProfit * 12;
    const annualNetProfit = monthlyNetProfit * 12;
    
    // ROI calculation (annual return on down payment)
    const roiPercentage = downPayment > 0 ? (annualNetProfit / downPayment) * 100 : 0;
    
    // Break-even (weeks needed to cover monthly costs)
    const weeklyProfit = monthlyNetProfit / 4.33;
    const breakEvenWeeks = totalMonthlyCosts / (weeklyRentPrice / 4.33);
    
    // Payback period (years to recover down payment from net profit)
    const paybackYears = monthlyNetProfit > 0 ? downPayment / (monthlyNetProfit * 12) : null;
    
    // Total interest paid over loan term
    const totalInterestPaid = (monthlyLoanPayment * numPayments) - loanAmount;
    
    // Fleet totals
    const fleetDownPayment = downPayment * numberOfCars;
    const fleetMonthlyIncome = monthlyRentalIncome * numberOfCars;
    const fleetMonthlyCosts = totalMonthlyCosts * numberOfCars;
    const fleetMonthlyNetProfit = monthlyNetProfit * numberOfCars;
    const fleetAnnualNetProfit = annualNetProfit * numberOfCars;

    // Cash flow timeline (5 years)
    const cashFlowTimeline = [];
    for (let year = 1; year <= 5; year++) {
      cashFlowTimeline.push({
        year,
        income: Math.round(annualRentalIncome * numberOfCars),
        costs: Math.round(totalMonthlyCosts * 12 * numberOfCars),
        profit: Math.round(annualNetProfit * numberOfCars)
      });
    }

    // Cost breakdown for pie chart
    const costBreakdown = [
      { name: 'Loan Payment', value: monthlyLoanPayment * numberOfCars },
      { name: 'Insurance', value: monthlyInsurance * numberOfCars },
      { name: 'Maintenance', value: monthlyMaintenance * numberOfCars },
      { name: 'Other Costs', value: otherMonthlyCosts * numberOfCars }
    ];

    setResults({
      // Per car
      downPayment,
      loanAmount,
      monthlyLoanPayment,
      monthlyRentalIncome,
      monthlyInsurance,
      totalMonthlyCosts,
      monthlyGrossProfit,
      monthlyNetProfit,
      annualRentalIncome,
      annualNetProfit,
      roiPercentage,
      breakEvenWeeks,
      paybackYears,
      totalInterestPaid,
      // Fleet totals
      fleetDownPayment,
      fleetMonthlyIncome,
      fleetMonthlyCosts,
      fleetMonthlyNetProfit,
      fleetAnnualNetProfit,
      // Charts
      cashFlowTimeline,
      costBreakdown,
      numberOfCars
    });
  };

  useEffect(() => {
    calculateFleet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePresetChange = (presetName) => {
    const preset = CAR_PRESETS.find(p => p.name === presetName);
    if (preset && preset.name !== 'Custom') {
      setFormData(prev => ({
        ...prev,
        carPreset: presetName,
        carPrice: preset.price,
        weeklyRentPrice: preset.weeklyRent
      }));
    } else {
      setFormData(prev => ({ ...prev, carPreset: presetName }));
    }
  };

  const formatPLN = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B1437] via-[#1a2556] to-[#0B1437]" data-testid="taxi-fleet-analytics">
      <div className="border-b border-white/10 px-4 sm:px-6 py-6 bg-[#0B1437]/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl md:text-4xl tracking-tight font-semibold text-white mb-1" data-testid="taxi-fleet-title">Taxi Fleet Analytics</h1>
          <p className="text-sm text-slate-400">Uber/Bolt Car Rental Business Calculator (Warsaw)</p>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Car Preset Selection */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <label className="text-xs font-medium uppercase text-slate-400 mb-3 block tracking-wider">Car Model</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {CAR_PRESETS.map((preset) => (
              <button
                key={preset.name}
                onClick={() => handlePresetChange(preset.name)}
                data-testid={`car-preset-${preset.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                  formData.carPreset === preset.name
                    ? 'bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white shadow-lg'
                    : 'bg-[#1a2556]/60 border border-white/10 text-slate-300 hover:border-[#FF6B35]/50'
                }`}
                style={formData.carPreset === preset.name ? { boxShadow: '0 0 20px rgba(255, 107, 53, 0.3)' } : {}}
              >
                {preset.name}
              </button>
            ))}
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Input Section */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-4 space-y-4"
          >
            {/* Car & Financing */}
            <div className="card-premium rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Car size={20} weight="duotone" className="text-[#FF6B35]" />
                <h3 className="text-base font-semibold text-white">Car & Financing</h3>
              </div>
              <div className="space-y-3">
                <InputField
                  label="Car Price (PLN)"
                  value={formData.carPrice}
                  onChange={(e) => handleInputChange('carPrice', e.target.value)}
                  testId="input-car-price"
                />
                <InputField
                  label="Down Payment (%)"
                  value={formData.downPaymentPercent}
                  onChange={(e) => handleInputChange('downPaymentPercent', e.target.value)}
                  testId="input-down-payment"
                />
                <InputField
                  label="Interest Rate (%)"
                  value={formData.interestRate}
                  onChange={(e) => handleInputChange('interestRate', e.target.value)}
                  step="0.1"
                  testId="input-interest-rate"
                />
                <InputField
                  label="Loan Term (years)"
                  value={formData.loanTermYears}
                  onChange={(e) => handleInputChange('loanTermYears', e.target.value)}
                  testId="input-loan-term"
                />
                <InputField
                  label="Number of Cars"
                  value={formData.numberOfCars}
                  onChange={(e) => handleInputChange('numberOfCars', e.target.value)}
                  testId="input-number-of-cars"
                />
              </div>
            </div>

            {/* Rental Income */}
            <div className="card-premium rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Users size={20} weight="duotone" className="text-[#FF6B35]" />
                <h3 className="text-base font-semibold text-white">Rental Income</h3>
              </div>
              <div className="space-y-3">
                <InputField
                  label="Weekly Rent (PLN)"
                  value={formData.weeklyRentPrice}
                  onChange={(e) => handleInputChange('weeklyRentPrice', e.target.value)}
                  testId="input-weekly-rent"
                />
                <InputField
                  label="Utilization (weeks/year)"
                  value={formData.utilizationWeeks}
                  onChange={(e) => handleInputChange('utilizationWeeks', e.target.value)}
                  testId="input-utilization-weeks"
                />
              </div>
            </div>

            {/* Operating Costs */}
            <div className="card-premium rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Wrench size={20} weight="duotone" className="text-[#FF6B35]" />
                <h3 className="text-base font-semibold text-white">Operating Costs</h3>
              </div>
              <div className="space-y-3">
                <InputField
                  label="Annual Insurance (PLN)"
                  value={formData.annualInsurance}
                  onChange={(e) => handleInputChange('annualInsurance', e.target.value)}
                  testId="input-annual-insurance"
                />
                <InputField
                  label="Monthly Maintenance (PLN)"
                  value={formData.monthlyMaintenance}
                  onChange={(e) => handleInputChange('monthlyMaintenance', e.target.value)}
                  testId="input-monthly-maintenance"
                />
                <InputField
                  label="Other Monthly Costs (PLN)"
                  value={formData.otherMonthlyCosts}
                  onChange={(e) => handleInputChange('otherMonthlyCosts', e.target.value)}
                  testId="input-other-costs"
                />
                <InputField
                  label="Tax Rate (%)"
                  value={formData.taxRate}
                  onChange={(e) => handleInputChange('taxRate', e.target.value)}
                  testId="input-tax-rate"
                />
              </div>
            </div>
          </motion.div>

          {/* Results Section */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-8 space-y-4"
          >
            {results && (
              <>
                {/* Fleet Summary Card */}
                {results.numberOfCars > 1 && (
                  <div className="card-premium rounded-2xl p-5 border-[#FF6B35]/30">
                    <h3 className="text-lg font-semibold text-[#FF6B35] mb-4">Fleet Summary ({results.numberOfCars} Cars)</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-slate-400 uppercase">Total Investment</p>
                        <p className="text-xl font-mono font-bold text-white">{formatPLN(results.fleetDownPayment)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 uppercase">Monthly Income</p>
                        <p className="text-xl font-mono font-bold text-[#22c55e]">{formatPLN(results.fleetMonthlyIncome)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 uppercase">Monthly Costs</p>
                        <p className="text-xl font-mono font-bold text-[#ef4444]">{formatPLN(results.fleetMonthlyCosts)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 uppercase">Monthly Profit</p>
                        <p className={`text-xl font-mono font-bold ${results.fleetMonthlyNetProfit >= 0 ? 'text-[#22c55e]' : 'text-[#ef4444]'}`}>
                          {formatPLN(results.fleetMonthlyNetProfit)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Per Car Metrics */}
                <div className="card-premium rounded-2xl p-5">
                  <h3 className="text-lg font-semibold text-white mb-4">Per Car Analysis</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <CompactMetric
                      label="Down Payment"
                      value={formatPLN(results.downPayment)}
                      subtitle="Initial Investment"
                      positive={true}
                      testId="metric-down-payment"
                    />
                    <CompactMetric
                      label="Loan Amount"
                      value={formatPLN(results.loanAmount)}
                      subtitle="To Finance"
                      positive={false}
                      testId="metric-loan-amount"
                    />
                    <CompactMetric
                      label="Monthly Payment"
                      value={formatPLN(results.monthlyLoanPayment)}
                      subtitle="Loan + Interest"
                      positive={false}
                      testId="metric-monthly-payment"
                    />
                    <CompactMetric
                      label="Monthly Income"
                      value={formatPLN(results.monthlyRentalIncome)}
                      subtitle="From Driver"
                      positive={true}
                      testId="metric-monthly-income"
                    />
                    <CompactMetric
                      label="Monthly Costs"
                      value={formatPLN(results.totalMonthlyCosts)}
                      subtitle="All Expenses"
                      positive={false}
                      testId="metric-monthly-costs"
                    />
                    <CompactMetric
                      label="Monthly Profit"
                      value={formatPLN(results.monthlyNetProfit)}
                      subtitle="After Tax"
                      positive={results.monthlyNetProfit >= 0}
                      testId="metric-monthly-profit"
                    />
                    <CompactMetric
                      label="Annual Profit"
                      value={formatPLN(results.annualNetProfit)}
                      subtitle="Net/Year"
                      positive={results.annualNetProfit >= 0}
                      testId="metric-annual-profit"
                    />
                    <CompactMetric
                      label="ROI"
                      value={`${results.roiPercentage.toFixed(1)}%`}
                      subtitle="Annual Return"
                      positive={results.roiPercentage > 0}
                      testId="metric-roi"
                    />
                    <CompactMetric
                      label="Payback"
                      value={results.paybackYears ? `${results.paybackYears.toFixed(1)} yrs` : 'N/A'}
                      subtitle="Break-even"
                      positive={results.paybackYears && results.paybackYears < 5}
                      testId="metric-payback"
                    />
                  </div>
                </div>

                {/* Cash Flow Chart */}
                <div className="card-premium rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <ChartLineUp size={20} weight="duotone" className="text-[#FF6B35]" />
                    <h3 className="text-base font-semibold text-white">5-Year Cash Flow Projection</h3>
                  </div>
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={results.cashFlowTimeline}>
                      <defs>
                        <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#FF6B35" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#FF6B35" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="year" stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                      <YAxis stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                      <Tooltip 
                        formatter={(value) => formatPLN(value)}
                        contentStyle={{ 
                          backgroundColor: '#1a2556', 
                          border: '1px solid rgba(255,107,53,0.2)',
                          borderRadius: '12px',
                          fontSize: '12px'
                        }}
                      />
                      <Area type="monotone" dataKey="income" stroke="#22c55e" strokeWidth={2} fill="none" name="Income" />
                      <Area type="monotone" dataKey="costs" stroke="#ef4444" strokeWidth={2} fill="none" name="Costs" />
                      <Area type="monotone" dataKey="profit" stroke="#FF6B35" strokeWidth={2} fill="url(#colorProfit)" name="Net Profit" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Cost Breakdown */}
                  <div className="card-premium rounded-2xl p-5">
                    <h3 className="text-base font-semibold text-white mb-4">Monthly Cost Breakdown</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={results.costBreakdown}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={3}
                          dataKey="value"
                        >
                          {results.costBreakdown.map((entry, index) => (
                            <Cell key={`pie-${entry.name}`} fill={[COLORS.primary, COLORS.secondary, COLORS.danger, COLORS.muted][index]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value) => formatPLN(value)}
                          contentStyle={{ 
                            backgroundColor: '#1a2556', 
                            border: '1px solid rgba(255,107,53,0.2)',
                            borderRadius: '12px',
                            fontSize: '12px'
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="flex flex-wrap justify-center gap-3 mt-2">
                      {results.costBreakdown.map((item, index) => (
                        <div key={item.name} className="flex items-center gap-1.5">
                          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: [COLORS.primary, COLORS.secondary, COLORS.danger, COLORS.muted][index] }}></div>
                          <span className="text-xs text-slate-400">{item.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Monthly P&L */}
                  <div className="card-premium rounded-2xl p-5">
                    <h3 className="text-base font-semibold text-white mb-4">Monthly P&L (Per Car)</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={[
                        { name: 'Income', value: results.monthlyRentalIncome },
                        { name: 'Loan', value: results.monthlyLoanPayment },
                        { name: 'Insurance', value: results.monthlyInsurance },
                        { name: 'Maint.', value: parseFloat(formData.monthlyMaintenance) }
                      ]}>
                        <XAxis dataKey="name" stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                        <YAxis stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                        <Tooltip 
                          formatter={(value) => formatPLN(value)}
                          contentStyle={{ 
                            backgroundColor: '#1a2556', 
                            border: '1px solid rgba(255,107,53,0.2)',
                            borderRadius: '12px',
                            fontSize: '12px'
                          }}
                        />
                        <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                          <Cell fill={COLORS.success} />
                          <Cell fill={COLORS.danger} />
                          <Cell fill={COLORS.secondary} />
                          <Cell fill={COLORS.muted} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Key Insights */}
                <div className="card-premium rounded-2xl p-5">
                  <h3 className="text-base font-semibold text-white mb-3">Key Insights</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div className="flex items-start gap-2">
                      <div className={`w-2 h-2 rounded-full mt-1.5 ${results.monthlyNetProfit >= 0 ? 'bg-[#22c55e]' : 'bg-[#ef4444]'}`}></div>
                      <p className="text-slate-300">
                        {results.monthlyNetProfit >= 0 
                          ? `Profitable: ${formatPLN(results.monthlyNetProfit)}/month per car after all costs and taxes.`
                          : `Loss: ${formatPLN(Math.abs(results.monthlyNetProfit))}/month per car. Consider higher rent or lower costs.`}
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="w-2 h-2 rounded-full mt-1.5 bg-[#FF6B35]"></div>
                      <p className="text-slate-300">
                        Break-even requires {results.breakEvenWeeks.toFixed(1)} rented weeks/month. You're utilizing {formData.utilizationWeeks}/52 weeks.
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="w-2 h-2 rounded-full mt-1.5 bg-[#F7931E]"></div>
                      <p className="text-slate-300">
                        Total interest paid over {formData.loanTermYears} years: {formatPLN(results.totalInterestPaid)}
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className={`w-2 h-2 rounded-full mt-1.5 ${results.roiPercentage > 15 ? 'bg-[#22c55e]' : 'bg-[#F7931E]'}`}></div>
                      <p className="text-slate-300">
                        ROI of {results.roiPercentage.toFixed(1)}% on down payment. {results.roiPercentage > 15 ? 'Good return!' : 'Consider negotiating better terms.'}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        </div>
      </main>
    </div>
  );
};

const InputField = ({ label, value, onChange, step, testId }) => (
  <div>
    <label className="text-xs font-medium uppercase text-slate-400 mb-1.5 block tracking-wider">{label}</label>
    <input
      type="number"
      value={value}
      onChange={onChange}
      step={step}
      data-testid={testId}
      className="w-full bg-[#0B1437]/80 border border-white/10 rounded-xl h-10 px-3 text-white text-sm focus:outline-none focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] transition-all duration-200"
    />
  </div>
);

const CompactMetric = ({ label, value, subtitle, positive, testId }) => (
  <div className="p-3 rounded-xl bg-[#1a2556]/60 backdrop-blur-xl border border-[#FF6B35]/20" data-testid={testId}>
    <p className="text-xs font-medium uppercase text-slate-400 mb-1 truncate">{label}</p>
    <p className="text-lg font-mono font-bold text-white mb-0.5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
      {value}
    </p>
    <span className={`text-xs font-semibold ${positive ? 'text-[#22c55e]' : 'text-[#ef4444]'}`}>
      {subtitle}
    </span>
  </div>
);

export default TaxiFleetAnalytics;
